import { motion } from "framer-motion";
import { Refrigerator, Flame, UtensilsCrossed, WashingMachine, Wine, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/data";
import Reveal from "@/components/Reveal";
import { scrollToHash } from "@/components/Navbar";

const BIG = [
  {
    id: "services-card-refrigeration",
    num: "01",
    icon: Refrigerator,
    title: "Refrigeration & Freezers",
    blurb: "Compressor, sealed-system and temperature calibration for built-in and column refrigeration.",
    tags: "Sub-Zero · Thermador · Miele",
    img: IMAGES.ovenDetail,
    span: "lg:col-span-7",
  },
  {
    id: "services-card-cooking",
    num: "02",
    icon: Flame,
    title: "Ranges, Ovens & Cooktops",
    blurb: "Ignition, convection and induction systems tuned back to factory flame and heat curves.",
    tags: "Wolf · Viking · La Cornue",
    img: IMAGES.range,
    span: "lg:col-span-5",
  },
];

const SMALL = [
  {
    id: "services-card-dishwashers",
    num: "03",
    icon: UtensilsCrossed,
    title: "Dishwashers",
    blurb: "Pump, rack and panel repairs with leak-proof seals and quiet-cycle restoration.",
    tags: "Bosch · Miele · Gaggenau",
  },
  {
    id: "services-card-laundry",
    num: "04",
    icon: WashingMachine,
    title: "Laundry Systems",
    blurb: "Drum balancing, bearing replacement and vent-safe dryer service for premium laundry pairs.",
    tags: "Miele · Bosch · Jenn-Air",
  },
  {
    id: "services-card-wine-ice",
    num: "05",
    icon: Wine,
    title: "Wine & Ice",
    blurb: "Climate-precise wine columns and clear-ice systems serviced to cellar-grade stability.",
    tags: "Sub-Zero · Dacor · U-Line",
  },
];

function BigCard({ s, i }) {
  return (
    <Reveal delay={i * 0.12} className={`col-span-1 md:col-span-8 ${s.span}`}>
      <div
        data-testid={s.id}
        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#171B20] h-full min-h-[340px] transition-colors duration-500 hover:border-[#E06A26]/40"
      >
        <img
          src={s.img}
          alt={s.title}
          className="absolute inset-0 w-full h-full object-cover img-spot opacity-45 transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1215] via-[#0F1215]/55 to-transparent" />
        <div className="relative p-8 sm:p-10 flex flex-col justify-end h-full min-h-[340px]">
          <p className="font-mono text-[11px] tracking-[0.3em] text-[#E06A26] mb-3">{s.num} /</p>
          <s.icon className="w-6 h-6 text-[#F4F2EE] mb-4" strokeWidth={1.5} />
          <h3 className="font-display text-xl sm:text-2xl font-semibold mb-3">{s.title}</h3>
          <p className="text-sm sm:text-base text-[#94A0AC] leading-relaxed max-w-md">{s.blurb}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#606D7A] mt-4">{s.tags}</p>
        </div>
      </div>
    </Reveal>
  );
}

function SmallCard({ s, i }) {
  return (
    <Reveal delay={i * 0.12} className="col-span-1 md:col-span-4">
      <div
        data-testid={s.id}
        className="group relative rounded-2xl border border-white/10 bg-[#171B20]/90 backdrop-blur-sm p-8 h-full transition-all duration-500 hover:border-[#E06A26]/40 hover:-translate-y-1.5"
      >
        <div className="flex items-start justify-between mb-8">
          <p className="font-mono text-[11px] tracking-[0.3em] text-[#E06A26]">{s.num} /</p>
          <s.icon className="w-6 h-6 text-[#F4F2EE]/80 group-hover:text-[#E06A26] transition-colors duration-500" strokeWidth={1.5} />
        </div>
        <h3 className="font-display text-xl font-semibold mb-3">{s.title}</h3>
        <p className="text-sm text-[#94A0AC] leading-relaxed">{s.blurb}</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#606D7A] mt-5">{s.tags}</p>
      </div>
    </Reveal>
  );
}

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E06A26] mb-5">What we service</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl">
            Every appliance your home depends on, restored to factory spec.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 lg:gap-8 mt-14">
          {BIG.map((s, i) => <BigCard key={s.id} s={s} i={i} />)}
          {SMALL.map((s, i) => <SmallCard key={s.id} s={s} i={i} />)}

          <Reveal className="col-span-1 md:col-span-8 lg:col-span-12">
            <button
              data-testid="services-card-cta"
              onClick={() => scrollToHash("#contact")}
              className="group w-full text-left rounded-2xl border border-dashed border-white/15 hover:border-[#E06A26]/50 p-8 flex flex-wrap items-center justify-between gap-4 transition-colors duration-500"
            >
              <p className="text-base sm:text-lg text-[#94A0AC]">
                Ventilation hoods, disposals, warming drawers —{" "}
                <span className="text-[#F4F2EE]">if it runs your home, we service it.</span>
              </p>
              <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#E06A26]">
                Request a repair
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
